
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { getChallenge } from '../../lib/api';
import { useToast } from '@/components/ui/use-toast';

export function useChallengeState() {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [votedPromptId, setVotedPromptId] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentMatchup, setCurrentMatchup] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [promptVotes, setPromptVotes] = useState({});
  const [hill, setHill] = useState(null);
  const [challengers, setChallengers] = useState([]);
  const [currentChallenger, setCurrentChallenger] = useState(null);

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch challenge data, reset hill & challengers in voting phase
  useEffect(() => {
    const fetchChallenge = async () => {
      setIsLoading(true);
      try {
        const data = await getChallenge();
        setChallenge(data);
        if (data.promptOptions) {
          const newVotes = {};
          data.promptOptions.forEach((p) => {
            newVotes[p.id] = p.votes;
          });
          setPromptVotes(newVotes);
        }
        if (user && data.submissions.some(s => s.userId === user.id)) {
          setHasSubmitted(true);
        }
        if (data.status === "voting-submissions" && user && data.submissions.length > 1) {
          const shuffled = [...data.submissions].sort(() => 0.5 - Math.random());
          setHill(shuffled[0]);
          setChallengers(shuffled.slice(1));
          setCurrentChallenger(shuffled[1]);
        }
      } catch (error) {
        console.error('Error fetching challenge:', error);
        toast({
          title: "Error",
          description: "Could not load the challenge. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchChallenge();
  }, [refreshKey, user?.id]);

  useEffect(() => {
    if (challenge?.status === "voting-submissions" && challengers.length > 0) {
      setCurrentChallenger(challengers[0]);
    }
  }, [challengers, challenge?.status]);

  const handleRefreshStatus = () => setRefreshKey(prev => prev + 1);

  return {
    challenge, setChallenge,
    isLoading,
    votedPromptId, setVotedPromptId,
    hasSubmitted, setHasSubmitted,
    isCameraOpen, setIsCameraOpen,
    currentMatchup, setCurrentMatchup,
    isVoting, setIsVoting,
    promptVotes, setPromptVotes,
    hill, setHill,
    challengers, setChallengers,
    currentChallenger, setCurrentChallenger,
    user, isAuthenticated,
    handleRefreshStatus,
  };
}
